// store/scenariosSlice.ts — V5
//
// Five canonical scenario types. V5 sharpens default data per the product spec:
//  - save-more ("Invest More") gains a `destination` field (invested | cash-savings).
//  - earn-more uses honest defaults: 25% tax, 50% invested.
//  - spend-less gains `investFreedCash` (default true).
//  - A one-time migration converts V4 data forward (adds new fields with safe
//    defaults) and older six-type vocabulary as before.

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { activeScenariosKey, isAnonymousSession } from '../lib/sessionMode';
import { primePlanShadow, syncListPlans, syncPutPlan } from '../lib/syncApi';
import type { DurabilityRecord } from '../pages/Simulate/durability';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ScenarioType =
	| 'save-more'
	| 'earn-more'
	| 'spend-less'
	| 'coast-fi'
	| 'custom';

export type SyncStatus = 'local' | 'pending' | 'synced' | 'error';

export interface ScenarioInstance {
	id: string;
	type: ScenarioType;
	name: string;
	colorIndex: number;
	data: Record<string, string>;
	createdAt: number;
	notes?: string;
	// ── V7 Phase 4: plan-of-record attributes (all optional, additive).
	// A saved plan's DELTA re-evaluates live against current base numbers;
	// these attributes are the plan's own frozen lens. Stored as strings
	// where numeric, matching the slice's string-field convention.
	/** The plan's declared target age ('' / absent = none). */
	targetFreedomAge?: string;
	/** Adopted Lab volatility as a decimal string, e.g. '0.15'. */
	mcVolatility?: string;
	mcDistribution?: 'normal' | 'student-t';
	/** Effort framing for shares, decimal string, e.g. '0.06'. */
	requiredShareOfIncome?: string;
	// V11: the plan's last Lab stress-test result (all optional/additive).
	/** Hold rate as a 0..100 integer string, e.g. '82'. */
	durabilityHoldRate?: string;
	/** ISO/epoch-ms timestamp the test was run, as a string. */
	durabilityTestedAt?: string;
	/** Hash of the plan state + Lab config the result was measured on. */
	durabilityConfigHash?: string;
	// V11: authored levers as the plan's source of truth (option a). Opaque
	// JSON array (LeverModel[]) so year-by-year + Details round-trip on reopen.
	/** Authored levers (LeverModel[]) as an opaque JSON array. */
	levers?: unknown[];
	// V13 / Moving Out drop 3: the plan's question instances. Opaque
	// (PlanQuestionInstance[]) for the same reason `levers` is: the slice
	// stays ignorant of the Simulate shapes, and questionViews stays
	// import-free. Additive and optional, so the migration's
	// spread-the-original-first carries it without a new case.
	//
	// IT IS WRITTEN THROUGH updateScenarioLevers AND NOWHERE ELSE. An
	// instance's `leverIds` point at levers in the same payload; a separate
	// action would leave a window where the ids reference moves that do not
	// exist yet, or moves that are already gone.
	/** Question instances (PlanQuestionInstance[]) as an opaque JSON array. */
	planQuestions?: unknown[];
	/** The user's default plan. Conflict rule for synced rows: highest
	 *  defaultSetAt wins at read time (see selectDefaultPlan). */
	isDefault?: boolean;
	defaultSetAt?: number;
	_syncStatus: SyncStatus;
	_remoteId?: string;
}

/** Plan-attribute subset used by add/update payloads. */
export interface PlanMeta extends Partial<DurabilityRecord> {
	targetFreedomAge?: string;
	mcVolatility?: string;
	mcDistribution?: 'normal' | 'student-t';
	requiredShareOfIncome?: string;
}

/** Read-time resolution of the default plan. Last-write-wins sync can leave
 *  two rows flagged; the most recently SET default wins, deterministically,
 *  without a write. */
export function selectDefaultPlan(
	scenarios: ScenarioInstance[],
): ScenarioInstance | null {
	let best: ScenarioInstance | null = null;
	for (const sc of scenarios) {
		if (!sc.isDefault) continue;
		if (!best || (sc.defaultSetAt ?? 0) > (best.defaultSetAt ?? 0))
			best = sc;
	}
	return best;
}

export interface ScenariosState {
	scenarios: ScenarioInstance[];
	_lastUpdated: number;
	_schemaVersion: number;
	_syncStatus: 'idle' | 'pending' | 'synced' | 'error';
}

// V7: the plan and question fields of the Moving Out slice. The bump is
// safe ONLY because migrateScenario above spreads the original before
// overlaying its reconstruction. Read contract section 13 before moving
// this number again: the bump is what re-runs every stored scenario
// through the migration, and a migration that drops fields writes the
// loss back to storage as a success.
export const SCENARIO_SCHEMA_VERSION = 7;

// ─── Default data per type (canonical field names) ─────────────────────────────

export const DEFAULT_SCENARIO_DATA: Record<
	ScenarioType,
	Record<string, string>
> = {
	'save-more': {
		extraMonthlyContribution: '300',
		destination: 'invested', // 'invested' | 'cash-savings'
		startAge: '',
		endAge: '',
	},
	'earn-more': {
		extraMonthlyIncome: '800',
		taxRatePercent: '25',
		percentInvested: '50',
		isPermanent: 'true',
		startAge: '',
		endAge: '',
		endYears: '3',
	},
	'spend-less': {
		monthlyExpenseReduction: '400',
		isPermanentLifestyleChange: 'true',
		investFreedCash: 'true',
		startAge: '',
		endAge: '',
	},
	'coast-fi': {
		currentInvestedAssets: '',
		futureRetirementAge: '65',
		expectedReturn: '',
		annualExpenses: '',
		withdrawalRate: '',
	},
	custom: {
		oneTimeContribution: '0',
		monthlyContributionChange: '0',
		incomeChange: '0',
		expenseChange: '0',
		returnOverride: '',
		startAge: '',
		endAge: '',
	},
};

// The storage key is mode-aware (nisk_scenarios vs nisk_scenarios_anon) —
// anonymous visitors save plans on Simulate into their own world. See
// lib/sessionMode.activeScenariosKey().

// ─── Migration ──────────────────────────────────────────────────────────────────

type AnyScenario = Record<string, unknown> & {
	type?: string;
	data?: Record<string, string>;
};

// Forward-fill a V4 scenario's data to V5 (add new fields, keep user values).
function upgradeV4Data(
	type: ScenarioType,
	d: Record<string, string>,
): Record<string, string> {
	const merged = { ...DEFAULT_SCENARIO_DATA[type], ...d };
	switch (type) {
		case 'save-more':
			// V4 had no destination → default to invested.
			merged.destination = d.destination ?? 'invested';
			break;
		case 'earn-more':
			// V4 stored taxHaircut; map to taxRatePercent if present.
			if (d.taxHaircut && !d.taxRatePercent)
				merged.taxRatePercent = d.taxHaircut;
			merged.percentInvested = d.percentInvested ?? '50';
			break;
		case 'spend-less':
			merged.investFreedCash = d.investFreedCash ?? 'true';
			break;
	}
	return merged;
}

/** Exported for the drop 0 round-trip tripwire. Contract section 13
 *  step 1: the migration test must call this DIRECTLY, because routed
 *  through loadFromStorage it short-circuits on
 *  `version >= SCENARIO_SCHEMA_VERSION` and passes vacuously. */
export function migrateScenario(old: AnyScenario): ScenarioInstance | null {
	const d = old.data ?? {};
	const base = {
		id: String(old.id ?? Math.random().toString(36).slice(2, 8)),
		name: String(old.name ?? 'Scenario'),
		colorIndex: typeof old.colorIndex === 'number' ? old.colorIndex : 0,
		createdAt:
			typeof old.createdAt === 'number' ? old.createdAt : Date.now(),
		_syncStatus: 'local' as SyncStatus,
	};

	switch (old.type) {
		case 'save-more':
		case 'earn-more':
		case 'spend-less':
		case 'coast-fi':
		case 'custom':
			return {
				// Spread the ORIGINAL first, so every field the
				// reconstruction does not own survives: levers,
				// targetFreedomAge, the mc lens, the durability record,
				// isDefault, defaultSetAt, notes and _remoteId. `base`
				// and the explicit fields below still win, which is what
				// keeps the legacy remaps correct. Contract section 13
				// step 3. Do NOT invert these two lines.
				...(old as Partial<ScenarioInstance>),
				...base,
				type: old.type,
				data: upgradeV4Data(old.type, d),
			};

		// ── Legacy six-type vocabulary ──
		case 'aggressive-savings':
			return {
				// Spread the ORIGINAL first, so every field the
				// reconstruction does not own survives: levers,
				// targetFreedomAge, the mc lens, the durability record,
				// isDefault, defaultSetAt, notes and _remoteId. `base`
				// and the explicit fields below still win, which is what
				// keeps the legacy remaps correct. Contract section 13
				// step 3. Do NOT invert these two lines.
				...(old as Partial<ScenarioInstance>),
				...base,
				type: 'custom',
				data: {
					...DEFAULT_SCENARIO_DATA.custom,
					monthlyContributionChange: d.extraMonthlySavings ?? '0',
					expenseChange: d.monthlyExpenseCut ?? '0',
					startAge: d.startAge ?? '',
					endAge: d.endAge ?? '',
				},
			};

		case 'side-hustle':
			return {
				// Spread the ORIGINAL first, so every field the
				// reconstruction does not own survives: levers,
				// targetFreedomAge, the mc lens, the durability record,
				// isDefault, defaultSetAt, notes and _remoteId. `base`
				// and the explicit fields below still win, which is what
				// keeps the legacy remaps correct. Contract section 13
				// step 3. Do NOT invert these two lines.
				...(old as Partial<ScenarioInstance>),
				...base,
				type: 'earn-more',
				data: {
					...DEFAULT_SCENARIO_DATA['earn-more'],
					extraMonthlyIncome: d.monthlyIncome ?? '0',
					percentInvested: d.percentInvested ?? '50',
					taxRatePercent: '25',
					isPermanent: 'true',
					startAge: d.startAge ?? '',
					endAge: d.endAge ?? '',
				},
			};

		case 'second-job':
			return {
				// Spread the ORIGINAL first, so every field the
				// reconstruction does not own survives: levers,
				// targetFreedomAge, the mc lens, the durability record,
				// isDefault, defaultSetAt, notes and _remoteId. `base`
				// and the explicit fields below still win, which is what
				// keeps the legacy remaps correct. Contract section 13
				// step 3. Do NOT invert these two lines.
				...(old as Partial<ScenarioInstance>),
				...base,
				type: 'earn-more',
				data: {
					...DEFAULT_SCENARIO_DATA['earn-more'],
					extraMonthlyIncome: String(
						Math.round(
							(parseFloat(d.extraAnnualIncome ?? '0') || 0) / 12,
						),
					),
					percentInvested: '100',
					taxRatePercent: '25',
					isPermanent: d.isPermanent ?? 'true',
					endYears: d.temporaryYears ?? '3',
					startAge: d.startAge ?? '',
					endAge: d.endAge ?? '',
				},
			};

		case 'expense-slash':
			return {
				// Spread the ORIGINAL first, so every field the
				// reconstruction does not own survives: levers,
				// targetFreedomAge, the mc lens, the durability record,
				// isDefault, defaultSetAt, notes and _remoteId. `base`
				// and the explicit fields below still win, which is what
				// keeps the legacy remaps correct. Contract section 13
				// step 3. Do NOT invert these two lines.
				...(old as Partial<ScenarioInstance>),
				...base,
				type: 'spend-less',
				data: {
					...DEFAULT_SCENARIO_DATA['spend-less'],
					monthlyExpenseReduction: d.monthlyExpenseCut ?? '0',
					isPermanentLifestyleChange: 'true',
					investFreedCash: 'true',
					startAge: d.startAge ?? '',
					endAge: d.endAge ?? '',
				},
			};

		default:
			return null;
	}
}

function loadFromStorage(): Partial<ScenariosState> | null {
	try {
		const raw = localStorage.getItem(activeScenariosKey());
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<ScenariosState> & {
			scenarios?: AnyScenario[];
		};

		const version = parsed._schemaVersion ?? 0;
		if (version >= SCENARIO_SCHEMA_VERSION) {
			return parsed as Partial<ScenariosState>;
		}

		const migrated = (parsed.scenarios ?? [])
			.map(migrateScenario)
			.filter((s): s is ScenarioInstance => s !== null);

		const next: Partial<ScenariosState> = {
			scenarios: migrated,
			_lastUpdated: Date.now(),
			_schemaVersion: SCENARIO_SCHEMA_VERSION,
		};
		try {
			localStorage.setItem(activeScenariosKey(), JSON.stringify(next));
		} catch {
			/* ignore */
		}
		return next;
	} catch {
		return null;
	}
}

function persistToStorage(state: ScenariosState) {
	try {
		localStorage.setItem(
			activeScenariosKey(),
			JSON.stringify({
				scenarios: state.scenarios,
				_lastUpdated: state._lastUpdated,
				_schemaVersion: SCENARIO_SCHEMA_VERSION,
			}),
		);
	} catch {
		/* quota / private browsing — fail silently */
	}
}

// ─── Initial state ────────────────────────────────────────────────────────────

const DEFAULT_STATE: ScenariosState = {
	scenarios: [],
	_lastUpdated: Date.now(),
	_schemaVersion: SCENARIO_SCHEMA_VERSION,
	_syncStatus: 'idle',
};

function buildInitialState(): ScenariosState {
	const stored = loadFromStorage();
	if (!stored) return DEFAULT_STATE;
	return {
		...DEFAULT_STATE,
		...stored,
		_schemaVersion: SCENARIO_SCHEMA_VERSION,
		_syncStatus: 'idle',
	};
}

// ─── Async thunks ────────────────────────────────────────────────────────────

export const fetchScenarios = createAsyncThunk(
	'scenarios/fetch',
	async (_, { rejectWithValue }) => {
		try {
			const local = loadFromStorage()?.scenarios ?? [];
			if (isAnonymousSession()) return local;

			// ── Authed: merge server plans with the local cache ──
			// Server wins per id (the freshest push across devices lives
			// there); local-only plans are pushed up so nothing is ever
			// silently dropped. Failures fall back to local — the local
			// world is always valid on its own.
			let server: Awaited<ReturnType<typeof syncListPlans>>;
			try {
				server = await syncListPlans();
			} catch {
				return local;
			}

			const merged: ScenarioInstance[] = server.map((p) => ({
				...(p.data as ScenarioInstance),
				_syncStatus: 'synced' as SyncStatus,
			}));
			const serverIds = new Set(server.map((p) => p.id));
			for (const sc of local) {
				if (serverIds.has(sc.id)) continue;
				merged.push(sc);
				try {
					const { _syncStatus: _t, ...doc } = sc;
					await syncPutPlan(sc.id, doc, Date.now());
				} catch {
					/* kept locally; middleware pushes on next edit */
				}
			}
			// The merged list becomes the new push baseline.
			primePlanShadow(merged);
			return merged;
		} catch {
			return rejectWithValue('Failed to load scenarios');
		}
	},
);

export const persistScenario = createAsyncThunk(
	'scenarios/persist',
	async (scenario: ScenarioInstance, { rejectWithValue }) => {
		try {
			return {
				...scenario,
				_syncStatus: 'synced' as SyncStatus,
				_remoteId: scenario._remoteId ?? scenario.id,
			};
		} catch {
			return rejectWithValue('Failed to save scenario');
		}
	},
);

export const deleteScenarioRemote = createAsyncThunk(
	'scenarios/deleteRemote',
	async (id: string) => id,
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 8);
const COLORS_COUNT = 5;

function nextColorIndex(scenarios: ScenarioInstance[]): number {
	const used = new Set(scenarios.map((s) => s.colorIndex));
	for (let i = 0; i < COLORS_COUNT; i++) if (!used.has(i)) return i;
	return scenarios.length % COLORS_COUNT;
}

// ─── Slice ────────────────────────────────────────────────────────────────────

const scenariosSlice = createSlice({
	name: 'scenarios',
	initialState: buildInitialState(),
	reducers: {
		addScenario(
			state,
			action: PayloadAction<{
				id?: string;
				type: ScenarioType;
				name: string;
				data?: Record<string, string>;
				notes?: string;
				/** V7 Phase 4 — plan-of-record attributes at save time. */
				plan?: PlanMeta;
			}>,
		) {
			const instance: ScenarioInstance = {
				id: action.payload.id ?? uid(),
				type: action.payload.type,
				name: action.payload.name,
				colorIndex: nextColorIndex(state.scenarios),
				data: {
					...DEFAULT_SCENARIO_DATA[action.payload.type],
					...action.payload.data,
				},
				createdAt: Date.now(),
				notes: action.payload.notes,
				...(action.payload.plan ?? {}),
				_syncStatus: 'local',
			};
			state.scenarios.push(instance);
			state._lastUpdated = Date.now();
			persistToStorage(state);
		},

		duplicateScenario(state, action: PayloadAction<string>) {
			const src = state.scenarios.find((s) => s.id === action.payload);
			if (!src) return;
			const copy: ScenarioInstance = {
				...src,
				id: uid(),
				name: `${src.name} (copy)`,
				colorIndex: nextColorIndex(state.scenarios),
				data: { ...src.data },
				createdAt: Date.now(),
				_syncStatus: 'local',
				_remoteId: undefined,
			};
			state.scenarios.push(copy);
			state._lastUpdated = Date.now();
			persistToStorage(state);
		},

		updateScenarioData(
			state,
			action: PayloadAction<{
				id: string;
				patch: Record<string, string>;
			}>,
		) {
			const sc = state.scenarios.find((s) => s.id === action.payload.id);
			if (sc) {
				Object.assign(sc.data, action.payload.patch);
				sc._syncStatus = 'local';
				state._lastUpdated = Date.now();
				persistToStorage(state);
			}
		},

		renameScenario(
			state,
			action: PayloadAction<{ id: string; name: string }>,
		) {
			const sc = state.scenarios.find((s) => s.id === action.payload.id);
			if (sc) {
				sc.name = action.payload.name;
				sc._syncStatus = 'local';
				state._lastUpdated = Date.now();
				persistToStorage(state);
			}
		},

		setScenarioNotes(
			state,
			action: PayloadAction<{ id: string; notes: string }>,
		) {
			const sc = state.scenarios.find((s) => s.id === action.payload.id);
			if (sc) {
				sc.notes = action.payload.notes;
				sc._syncStatus = 'local';
				state._lastUpdated = Date.now();
				persistToStorage(state);
			}
		},

		// ── V7 Phase 4 · plan-of-record reducers ──
		/** Update a plan's frozen lens (target / Lab config / effort). */
		updatePlanMeta(
			state,
			action: PayloadAction<{ id: string } & PlanMeta>,
		) {
			const sc = state.scenarios.find((s) => s.id === action.payload.id);
			if (!sc) return;
			const { id: _id, ...meta } = action.payload;
			Object.assign(sc, meta);
			sc._syncStatus = 'local';
			state._lastUpdated = Date.now();
			persistToStorage(state);
		},

		// V11 - authored levers as source of truth (option a).
		// V13 / Moving Out drop 3 - question instances ride along, and
		// `planQuestions` is REQUIRED rather than optional on purpose.
		// Optional would let a caller update levers while leaving stale
		// instances behind, which is the partial write this reducer exists
		// to make impossible. One action, one persist, one consistent pair.
		updateScenarioLevers(
			state,
			action: PayloadAction<{
				id: string;
				levers: unknown[];
				planQuestions: unknown[];
				data: Record<string, string>;
			}>,
		) {
			const sc = state.scenarios.find((s) => s.id === action.payload.id);
			if (!sc) return;
			sc.levers = action.payload.levers;
			sc.planQuestions = action.payload.planQuestions;
			sc.data = action.payload.data;
			sc._syncStatus = 'local';
			state._lastUpdated = Date.now();
			persistToStorage(state);
		},

		// V11 - Plan <-> Lab durability bridge.
		recordDurability(
			state,
			action: PayloadAction<{
				id: string;
				holdRate: string;
				testedAt: string;
				configHash: string;
			}>,
		) {
			const sc = state.scenarios.find((s) => s.id === action.payload.id);
			if (!sc) return;
			sc.durabilityHoldRate = action.payload.holdRate;
			sc.durabilityTestedAt = action.payload.testedAt;
			sc.durabilityConfigHash = action.payload.configHash;
			sc._syncStatus = 'local';
			state._lastUpdated = Date.now();
			persistToStorage(state);
		},

		/** Make one plan the default; clear the flag everywhere else
		 *  locally. defaultSetAt is the cross-device tiebreaker. */
		setDefaultPlan(state, action: PayloadAction<string>) {
			const now = Date.now();
			for (const sc of state.scenarios) {
				if (sc.id === action.payload) {
					sc.isDefault = true;
					sc.defaultSetAt = now;
					sc._syncStatus = 'local';
				} else if (sc.isDefault) {
					sc.isDefault = false;
					sc._syncStatus = 'local';
				}
			}
			state._lastUpdated = now;
			persistToStorage(state);
		},

		clearDefaultPlan(state) {
			for (const sc of state.scenarios) {
				if (sc.isDefault) {
					sc.isDefault = false;
					sc._syncStatus = 'local';
				}
			}
			state._lastUpdated = Date.now();
			persistToStorage(state);
		},

		removeScenario(state, action: PayloadAction<string>) {
			state.scenarios = state.scenarios.filter(
				(s) => s.id !== action.payload,
			);
			state._lastUpdated = Date.now();
			persistToStorage(state);
		},

		reorderScenarios(state, action: PayloadAction<string[]>) {
			const map = new Map(state.scenarios.map((s) => [s.id, s]));
			state.scenarios = action.payload
				.map((id) => map.get(id)!)
				.filter(Boolean);
			state._lastUpdated = Date.now();
			persistToStorage(state);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchScenarios.fulfilled, (state, action) => {
				state.scenarios = action.payload as ScenarioInstance[];
				state._syncStatus = 'synced';
			})
			.addCase(persistScenario.fulfilled, (state, action) => {
				const sc = state.scenarios.find(
					(s) => s.id === action.payload.id,
				);
				if (sc) Object.assign(sc, action.payload);
				persistToStorage(state);
			})
			.addCase(deleteScenarioRemote.fulfilled, (state, action) => {
				state.scenarios = state.scenarios.filter(
					(s) => s.id !== action.payload,
				);
				persistToStorage(state);
			});
	},
});

export const {
	addScenario,
	duplicateScenario,
	updateScenarioData,
	renameScenario,
	setScenarioNotes,
	removeScenario,
	reorderScenarios,
	updatePlanMeta,
	updateScenarioLevers,
	recordDurability,
	setDefaultPlan,
	clearDefaultPlan,
} = scenariosSlice.actions;

export default scenariosSlice.reducer;
